// The business's Supplier Particulars (#342): what every tax invoice it issues prints about it as the supplier. An
// Owner sees and changes them here; onboarding sets them first. Each change goes to the Audit Log, and invoices issued
// after it carry it. The GSTIN must be of the address's State.
import { useEffect, useState, type FormEvent } from "react";
import { ApiError, type Api, type SupplierParticulars as Particulars } from "../api.js";
import { useWords } from "../i18n/words.js";

/** Rupees as typed, e.g. "1,00,00,000" or "4999.50", in paise; undefined when it is not an amount. */
export function rupeesToPaise(typed: string): number | undefined {
  const plain = typed.replaceAll(",", "").trim();
  if (!/^\d+(\.\d{1,2})?$/.test(plain)) return undefined;
  return Math.round(Number(plain) * 100);
}

const paiseToRupees = (paise: number) => (paise % 100 === 0 ? String(paise / 100) : (paise / 100).toFixed(2));

/** The form as typed: every particular as text. */
interface Form {
  readonly legalName: string;
  readonly address: string;
  readonly state: string;
  readonly gstin: string;
  readonly signatory: string;
  readonly turnover: string;
}

const EMPTY: Form = { legalName: "", address: "", state: "", gstin: "", signatory: "", turnover: "" };
const formOf = (found: Particulars | null): Form =>
  found
    ? {
        legalName: found.legalName,
        address: found.address,
        state: found.state,
        gstin: found.gstin,
        signatory: found.signatory,
        turnover: paiseToRupees(found.aggregateTurnover),
      }
    : EMPTY;

export function SupplierParticulars({ api }: { api: Pick<Api, "supplierParticulars" | "setSupplierParticulars"> }) {
  const w = useWords();
  const [loaded, setLoaded] = useState(false);
  const [form, setForm] = useState<Form>(EMPTY);
  const [current, setCurrent] = useState<Particulars | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string>();
  const [problem, setProblem] = useState<string>();

  useEffect(() => {
    let live = true;
    api.supplierParticulars().then(
      (found) => {
        if (!live) return;
        setCurrent(found);
        setForm(formOf(found));
        setLoaded(true);
      },
      () => live && setLoaded(false),
    );
    return () => {
      live = false;
    };
  }, [api]);

  if (!loaded) return null;
  const { legalName, address, state, gstin, signatory, turnover } = form;
  const set = (key: keyof Form) => (event: { target: { value: string } }) =>
    setForm({ ...form, [key]: event.target.value });
  const paise = rupeesToPaise(turnover);
  const ready =
    legalName.trim() !== "" &&
    address.trim() !== "" &&
    /^\d{2}$/.test(state) &&
    gstin.trim() !== "" &&
    signatory.trim() !== "" &&
    paise !== undefined;

  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (paise === undefined) return;
    setBusy(true);
    setNotice(undefined);
    setProblem(undefined);
    try {
      const saved = await api.setSupplierParticulars({
        legalName: legalName.trim(),
        address: address.trim(),
        state,
        gstin: gstin.trim().toUpperCase(),
        signatory: signatory.trim(),
        aggregateTurnover: paise,
      });
      setCurrent(saved);
      setForm(formOf(saved));
      setNotice(w.particularsSaved);
    } catch (error) {
      setProblem(
        error instanceof ApiError && error.problems.length > 0 ? error.problems.join("; ") : w.settingsUnreachable,
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="section" aria-labelledby="particulars-heading">
      <h2 id="particulars-heading">{w.particularsHeading}</h2>
      <p className="small muted">{w.particularsHelp}</p>
      {current ? null : <p className="alert">{w.particularsNone}</p>}
      <form onSubmit={(event) => void save(event)}>
        <label className="field">
          {w.particularsLegalName} <input value={legalName} maxLength={200} onChange={set("legalName")} />
        </label>
        <label className="field">
          {/* The business's own address and State: proper nouns no language translates. */}
          {w.particularsAddress}{" "}
          <textarea value={address} maxLength={500} rows={2} translate="no" onChange={set("address")} />
        </label>
        <label className="field">
          {w.particularsState}{" "}
          <input value={state} maxLength={2} inputMode="numeric" autoComplete="off" onChange={set("state")} />
          {current?.stateName && current.state === state ? (
            <>
              {" "}
              <span translate="no">{current.stateName}</span>
            </>
          ) : null}
        </label>
        <label className="field">
          {w.particularsGstin} <input value={gstin} maxLength={15} autoComplete="off" onChange={set("gstin")} />
        </label>
        <label className="field">
          {w.particularsSignatory} <input value={signatory} maxLength={200} onChange={set("signatory")} />
        </label>
        <label className="field">
          {w.particularsTurnover} <input value={turnover} inputMode="decimal" onChange={set("turnover")} />
        </label>
        <button type="submit" className="btn" disabled={busy || !ready}>
          {w.particularsSave}
        </button>
      </form>
      {notice ? <p role="status">{notice}</p> : null}
      {problem ? (
        <p className="alert" role="alert">
          {problem}
        </p>
      ) : null}
    </section>
  );
}
